import React, { useEffect, useMemo, useState } from 'react'
import { Gem, Loader, XCircle, Package, Edit3, Copy } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import uzsApi, { UZS_PAYMENT_FLOW, openUzsPayment } from '../services/uzsApi'
import usePaymentStore from '../store/usePaymentStore'
import { closePreparedPaymentWindow, prepareExternalPaymentWindow } from '../utils/openExternalPaymentLink'

/**
 * UzsPayment
 * Оплата узбекскими сумами (UZS) через P2P перевод на карту
 * Поддерживает два режима: готовые пакеты и произвольная сумма (UZS/кристаллы)
 */
const UzsPayment = ({ method = 'uzs1' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isLinkOnly = ['uzs3', 'uzs4', 'uzs5', 'uzs6', 'uzs7'].includes(method)

  // Состояния для табов
  const [activeTab, setActiveTab] = useState('packages') // packages | custom

  // Конфигурация uzs1 (курсы и лимиты)
  const [uzsConfig, setUzsConfig] = useState(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [configError, setConfigError] = useState(null)

  // Пакеты
  const [packages, setPackages] = useState([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [packagesError, setPackagesError] = useState(null)

  // Произвольная сумма
  const [uzsInput, setUzsInput] = useState('')
  const [crystalsInput, setCrystalsInput] = useState('')
  const [customAmountError, setCustomAmountError] = useState('')
  const [lastEdited, setLastEdited] = useState('uzs') // uzs | crystals

  // Созданный платеж (реквизиты)
  const [paymentDetails, setPaymentDetails] = useState(null)

  // Обработка платежа
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingPackageId, setProcessingPackageId] = useState(null)

  useEffect(() => {
    loadUzsConfig()
  }, [method])

  useEffect(() => {
    loadPackages()
  }, [method])

  const rates = useMemo(() => {
    const priceOneCrystalInUzs = parseFloat(uzsConfig?.price_one_crystal_in_uzs || '0')
    const priceOneUzsInCrystal = parseFloat(uzsConfig?.price_one_uzs_in_crystal || '0')

    return {
      priceOneCrystalInUzs,
      priceOneUzsInCrystal
    }
  }, [uzsConfig])

  // Форматирование чисел с пробелами для отображения
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  const loadUzsConfig = async () => {
    setIsLoadingConfig(true)
    setConfigError(null)

    try {
      const config = await uzsApi.fetchUzsConfig(method)
      setUzsConfig(config)
      console.log('✅ UZS config loaded:', config)
    } catch (error) {
      console.error('❌ Error loading UZS config:', error)
      setConfigError(t('payment.sbp.configError'))
    } finally {
      setIsLoadingConfig(false)
    }
  }

  const loadPackages = async () => {
    setIsLoadingPackages(true)
    setPackagesError(null)

    try {
      const uzsPackages = await uzsApi.fetchUzsPackages(method)

      // Сортируем пакеты: самый дешевый первый, затем популярные
      const sortedPackages = [...uzsPackages].sort((a, b) => {
        const minPrice = Math.min(...uzsPackages.map((p) => p[method]))

        if (a[method] === minPrice && b[method] !== minPrice) return -1
        if (b[method] === minPrice && a[method] !== minPrice) return 1

        if (a.popular && !b.popular) return -1
        if (b.popular && !a.popular) return 1

        return a[method] - b[method]
      })

      setPackages(sortedPackages)
      console.log('✅ UZS packages loaded:', sortedPackages)
    } catch (error) {
      console.error('❌ Error loading UZS packages:', error)
      setPackagesError(t('payment.sbp.packagesError'))
    } finally {
      setIsLoadingPackages(false)
    }
  }

  const copyToClipboard = async (value, successText) => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API not available')
      }

      await navigator.clipboard.writeText(value)
      toast.success(successText)
    } catch (error) {
      console.error('❌ Ошибка копирования:', error)
      toast.error(t('payment.uzs.copyError'))
    }
  }

  const saveOrderIdToSession = (orderId) => {
    try {
      sessionStorage.setItem('uzs_order_id', orderId)
    } catch (error) {
      console.error('❌ Не удалось сохранить order_id в sessionStorage:', error)
    }
  }

  const handlePackagePayment = async (pkg) => {
    if (isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)
    setProcessingPackageId(pkg.id)

    try {
      console.log('💰 Создание платежа UZS для пакета:', pkg)

      // Для пакета всегда создаем платеж по кристаллам
      const payment = await uzsApi.createPaymentByCrystals(pkg.crystals, method)

      if (payment.mode === UZS_PAYMENT_FLOW.LINK) {
        openUzsPayment(payment.link, () => {
          toast.success(t('payment.sbp.paymentNotification'))
          setTimeout(() => {
            navigate('/')
          }, 1500)
        }, paymentWindow)
        return
      }

      closePreparedPaymentWindow(paymentWindow)

      setPaymentDetails(payment.data)
      saveOrderIdToSession(payment.data.order_id)

      toast.success(t('payment.uzs.transferHint'))
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа UZS:', error)

      if (error.message === 'INVALID_AMOUNT') {
        toast.error(uzsConfig ? t('payment.uzs.errorRange', { min: formatNumber(uzsConfig.min_uzs), max: formatNumber(uzsConfig.max_uzs) }) : t('payment.crypto.errorInvalidAmountShort'))
      } else if (error.message === 'LIMIT_ORDER' && isLinkOnly) {
        toast.error(t('payment.uzs3.errorLimitOrder'))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(isLinkOnly ? t('payment.uzs3.errorBackend') : t('payment.sbp.errorBackend'))
        if (['uzs4', 'uzs5', 'uzs6', 'uzs7'].includes(method)) usePaymentStore.getState().loadAvailableMethods()
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
      setProcessingPackageId(null)
    }
  }

  const handleUzsChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '')
    setLastEdited('uzs')
    setUzsInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneUzsInCrystal) {
      const uzs = parseFloat(value)
      if (Number.isFinite(uzs)) {
        const crystals = isLinkOnly ? Math.floor(uzs * rates.priceOneUzsInCrystal) : Math.round(uzs * rates.priceOneUzsInCrystal)
        setCrystalsInput(crystals.toString())
      } else {
        setCrystalsInput('')
      }
    } else {
      setCrystalsInput('')
    }
  }

  const handleCrystalsChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '')
    setLastEdited('crystals')
    setCrystalsInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneCrystalInUzs) {
      const crystals = parseFloat(value)
      if (Number.isFinite(crystals)) {
        const uzs = Math.round(crystals * rates.priceOneCrystalInUzs)
        setUzsInput(uzs.toString())
      } else {
        setUzsInput('')
      }
    } else {
      setUzsInput('')
    }
  }

  const validateCustomAmount = () => {
    if (!uzsInput || !uzsConfig) {
      setCustomAmountError(t('payment.sbp.enterAmount'))
      return false
    }

    const uzs = parseFloat(uzsInput)
    if (!Number.isFinite(uzs)) {
      setCustomAmountError(t('payment.crypto.enterCorrectAmount'))
      return false
    }

    if (uzs < uzsConfig.min_uzs || uzs > uzsConfig.max_uzs) {
      setCustomAmountError(
        t('payment.uzs.errorRange', {
          min: formatNumber(uzsConfig.min_uzs),
          max: formatNumber(uzsConfig.max_uzs)
        })
      )
      return false
    }

    return true
  }

  const handleCustomPayment = async () => {
    if (!validateCustomAmount() || isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)

    try {
      const uzs = parseInt(uzsInput || '0', 10)
      const crystals = parseInt(crystalsInput || '0', 10)

      console.log('💰 Создание платежа UZS (custom):', { uzs, crystals, lastEdited })

      const payment =
        lastEdited === 'crystals'
          ? await uzsApi.createPaymentByCrystals(crystals, method)
          : await uzsApi.createPaymentByUzs(uzs, method)

      if (payment.mode === UZS_PAYMENT_FLOW.LINK) {
        openUzsPayment(payment.link, () => {
          toast.success(t('payment.sbp.paymentNotification'))
          setTimeout(() => {
            navigate('/')
          }, 1500)
        }, paymentWindow)
        return
      }

      closePreparedPaymentWindow(paymentWindow)

      setPaymentDetails(payment.data)
      saveOrderIdToSession(payment.data.order_id)

      toast.success(t('payment.uzs.transferHint'))
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа UZS:', error)

      if (error.message === 'INVALID_AMOUNT') {
        setCustomAmountError(
          t('payment.uzs.errorRange', {
            min: formatNumber(uzsConfig.min_uzs),
            max: formatNumber(uzsConfig.max_uzs)
          })
        )
      } else if (error.message === 'LIMIT_ORDER' && isLinkOnly) {
        setCustomAmountError(t('payment.uzs3.errorLimitOrder'))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(isLinkOnly ? t('payment.uzs3.errorBackend') : t('payment.sbp.errorBackend'))
        if (['uzs4', 'uzs5', 'uzs6', 'uzs7'].includes(method)) usePaymentStore.getState().loadAvailableMethods()
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const renderPackages = () => {
    if (isLoadingPackages) {
      return (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="bg-surface rounded-lg border border-white border-opacity-10 p-4 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg"></div>
                  <div>
                    <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-16"></div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                  <div className="h-6 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (packagesError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{packagesError}</p>
          <button
            onClick={loadPackages}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('common.retry')}
          </button>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-4">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`relative bg-surface rounded-lg border border-white border-opacity-10 p-4 cursor-pointer transition-all hover:border-accent-primary/50 hover:scale-[1.02] active:scale-[0.98] ${
              pkg.popular ? 'ring-2 ring-accent-primary ring-opacity-50' : ''
            } ${
              isProcessing && processingPackageId === pkg.id
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={() => !isProcessing && handlePackagePayment(pkg)}
          >
            {pkg.popular && (
              <div className="absolute -top-2 -right-2">
                <span className="inline-block px-3 py-1 bg-accent-primary text-dark-primary text-xs font-bold rounded-full shadow-lg">
                  {t('payment.popular')}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gem size={28} className="text-dark-primary" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{formatNumber(pkg.crystals)}</p>
                  <p className="text-text-secondary text-sm">{t('payment.crypto.crystals')}</p>
                  {/* Бонусные кристаллы от промокода */}
                  {pkg.promo_bonus_crystals > 0 && (
                    <p className="text-accent-primary text-xs font-semibold mt-0.5">
                      +{formatNumber(pkg.promo_bonus_crystals)} {t('promo.bonus')}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right">
                {isProcessing && processingPackageId === pkg.id ? (
                  <div className="flex items-center gap-2 text-accent-primary">
                    <Loader size={20} className="animate-spin" />
                  </div>
                ) : (
                  <div className="text-accent-primary font-bold text-lg">
                    <span>{formatNumber(pkg[method])} UZS</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderCustomAmount = () => {
    if (isLoadingConfig) {
      return (
        <div className="text-center py-8">
          <Loader size={48} className="text-accent-primary mx-auto mb-4 animate-spin" />
          <p className="text-text-secondary">{t('payment.sbp.loadingConfig')}</p>
        </div>
      )
    }

    if (configError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{configError}</p>
          <button
            onClick={loadUzsConfig}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('common.retry')}
          </button>
        </div>
      )
    }

    const hasError = customAmountError !== ''
    const uzs = parseFloat(uzsInput)
    const isValidAmount =
      uzsInput &&
      !hasError &&
      Number.isFinite(uzs) &&
      uzs >= uzsConfig.min_uzs &&
      uzs <= uzsConfig.max_uzs

    return (
      <div className="space-y-6">
        <div className="text-center">
          <p className="text-text-secondary text-sm">
            {t('payment.uzs.limits', {
              min: formatNumber(uzsConfig.min_uzs),
              max: formatNumber(uzsConfig.max_uzs)
            })}
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{t('payment.uzs.amountUzsLabel')}</label>
          <input
            type="text"
            inputMode="numeric"
            value={uzsInput}
            onChange={handleUzsChange}
            placeholder={t('payment.sbp.enterAmount')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{t('payment.crypto.crystals')}</label>
          <input
            type="text"
            inputMode="numeric"
            value={crystalsInput}
            onChange={handleCrystalsChange}
            placeholder={t('payment.sbp.enterCrystals')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        {/* Промо-бонус для произвольной суммы */}
        {uzsConfig?.promo?.percent && crystalsInput && (
          <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-lg p-3 text-center">
            <span className="text-accent-primary font-semibold text-sm">
              +{formatNumber(Math.ceil(parseInt(crystalsInput) * uzsConfig.promo.percent / 100))} {t('promo.bonus')}
            </span>
          </div>
        )}

        {hasError && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm text-center">{customAmountError}</p>
          </div>
        )}

        <button
          onClick={handleCustomPayment}
          disabled={!isValidAmount || isProcessing}
          className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
            isValidAmount && !isProcessing
              ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader size={20} className="animate-spin" />
              {t('payment.uzs.creating')}
            </span>
          ) : (
            t(isLinkOnly ? 'payment.uzs3.openPayment' : 'payment.uzs.getDetails')
          )}
        </button>
      </div>
    )
  }

  if (paymentDetails) {
    const handleChooseAnotherPackage = () => {
      setPaymentDetails(null)
      setActiveTab('packages')
      setCustomAmountError('')
    }

    const handleIpaid = () => {
      toast.success(t('payment.uzs.paymentPendingToast'), { duration: 5000 })
      navigate('/')
    }

    const cardDisplay = paymentDetails.number_card
    const cardCopy = (paymentDetails.number_card || '').replace(/\s/g, '')
    const fio = paymentDetails.fio
    const uzsValue = String(paymentDetails.uzs ?? '')
    const uzsCopy = uzsValue.replace(/\s/g, '')

    return (
      <div className="max-w-md mx-auto space-y-4">
        <div className="bg-surface border border-white border-opacity-10 rounded-lg p-4">
          <p className="text-sm text-text-secondary">
            {t('payment.uzs.transferHint')}
          </p>
        </div>

        <div className="bg-surface border border-white border-opacity-10 rounded-lg p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-text-secondary">{t('payment.uzs.fields.cardNumber')}</p>
                <p className="text-white font-semibold">{cardDisplay}</p>
              </div>
              <button
                onClick={() => copyToClipboard(cardCopy, t('payment.uzs.copySuccess.cardNumber'))}
                className="px-3 py-2 bg-dark-primary rounded-lg border border-white border-opacity-10 hover:border-accent-primary/40 transition-colors"
                aria-label={t('payment.uzs.aria.copyCardNumber')}
              >
                <Copy size={18} className="text-accent-primary" />
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-text-secondary">{t('payment.uzs.fields.recipient')}</p>
                <p className="text-white font-semibold">{fio}</p>
              </div>
              <button
                onClick={() => copyToClipboard(fio, t('payment.uzs.copySuccess.recipient'))}
                className="px-3 py-2 bg-dark-primary rounded-lg border border-white border-opacity-10 hover:border-accent-primary/40 transition-colors"
                aria-label={t('payment.uzs.aria.copyRecipient')}
              >
                <Copy size={18} className="text-accent-primary" />
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-text-secondary">{t('payment.uzs.fields.amount')}</p>
                <p className="text-white font-semibold">{formatNumber(uzsCopy)} UZS</p>
              </div>
              <button
                onClick={() => copyToClipboard(uzsCopy, t('payment.uzs.copySuccess.amount'))}
                className="px-3 py-2 bg-dark-primary rounded-lg border border-white border-opacity-10 hover:border-accent-primary/40 transition-colors"
                aria-label={t('payment.uzs.aria.copyAmount')}
              >
                <Copy size={18} className="text-accent-primary" />
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs text-text-secondary">{t('payment.uzs.fields.orderId')}</p>
            <p className="text-white font-mono text-sm break-all">{paymentDetails.order_id}</p>
          </div>
        </div>

        <button
          onClick={handleIpaid}
          className="w-full py-4 rounded-lg font-bold text-lg bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90 transition-all"
        >
          {t('payment.uzs.iPaid')}
        </button>

        <button
          onClick={handleChooseAnotherPackage}
          disabled={isProcessing}
          className={`w-full py-3 rounded-lg font-semibold transition-all ${
            isProcessing
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-surface text-white border border-white border-opacity-10 hover:border-accent-primary/40'
          }`}
        >
          {t('payment.uzs.chooseAnotherPackage')}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('packages')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'packages'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Package size={18} className="inline mr-2" />
            {t('payment.crypto.tabPackages')}
          </button>

          <button
            onClick={() => setActiveTab('custom')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'custom'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Edit3 size={18} className="inline mr-2" />
            {t('payment.crypto.tabCustom')}
          </button>
        </div>
      </div>

      {activeTab === 'packages' ? renderPackages() : renderCustomAmount()}
    </div>
  )
}

export default UzsPayment
